import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FileUploadSection = ({ files, onChange, error }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e?.target?.files || []);
    const validFiles = selectedFiles?.filter(file => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      return validTypes?.includes(file?.type) && file?.size <= maxSize;
    });

    if (validFiles?.length > 0) {
      const newFiles = await Promise.all(validFiles?.map(async (file) => {
        let preview = null;
        let base64Data = null;
        
        if (file?.type?.startsWith('image/')) {
          base64Data = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(file);
          });
          preview = base64Data;
        }
        
        return {
          id: Date.now() + Math.random(),
          name: file?.name,
          size: file?.size,
          type: file?.type,
          preview: preview,
          base64: base64Data
        };
      }));
      onChange([...(Array.isArray(files) ? files : []), ...newFiles]);
    }
  };

  const removeFile = (fileId) => {
    const updatedFiles = files?.filter(f => f?.id !== fileId);
    onChange(updatedFiles);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Octets';
    const k = 1024;
    const sizes = ['Octets', 'Ko', 'Mo'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const triggerFileInput = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="glass-card rounded-2xl p-6 hover-glow hover-lift">
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-white mb-1">
          Documents
        </h2>
        <p className="text-sm text-gray-400">
          Radios, CBCT (PDF, JPG, PNG - Max 10 Mo)
        </p>
        <p className="text-sm text-yellow-400/90 mt-2">
          ⚠️ Ne pas joindre de documents avec nom/prénom du patient
        </p>
      </div>
      
      <div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileSelect}
          className="hidden"
        />

        <button
          type="button"
          onClick={triggerFileInput}
          className="w-full p-10 border-2 border-dashed border-white/20 rounded-2xl bg-white/5 hover:border-primary/50 hover:bg-white/8 hover:shadow-[0_0_24px_rgba(74,144,226,0.15)] transition-all flex flex-col items-center gap-4"
        >
          <div className="w-14 h-14 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center">
            <Icon name="Upload" size={28} className="text-primary" />
          </div>
          <div className="text-center">
            <p className="text-base font-medium text-white">Cliquez pour télécharger</p>
            <p className="text-sm text-gray-400 mt-1">ou glissez-déposez les fichiers</p>
          </div>
        </button>

        {error && (
          <p className="text-sm text-red-400 mt-3">{error}</p>
        )}
      </div>
      
      {files?.length > 0 && (
        <div className="mt-5 pt-5 border-t border-white/10">
          <div className="text-sm text-gray-400 mb-4">
            Fichiers téléchargés ({files?.length}) :
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {files?.map((file) => (
              <div key={file?.id} className="relative flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-xl hover:border-white/20 hover:bg-white/8 transition-all">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden">
                  {file?.preview ? (
                    <Image
                      src={file?.preview}
                      alt={`Preview of ${file?.name} radiograph`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon name="FileText" size={20} className="text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate font-medium">{file?.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{formatFileSize(file?.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(file?.id)}
                  className="flex-shrink-0 p-2 hover:bg-red-500/20 rounded-lg transition-all"
                  aria-label={`Supprimer ${file?.name}`}
                >
                  <Icon name="Trash2" size={16} className="text-red-400" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadSection;